import { AdMob } from '@capacitor-community/admob';
import { Capacitor } from '@capacitor/core';

const INTERSTITIAL_ID = 'ca-app-pub-1024349425942607/1687850059';
const APP_OPEN_ID = 'ca-app-pub-1024349425942607/3399035906';

// Frequency Limits (in milliseconds)
const LIMITS = {
    interstitial: 1000 * 60 * 60, // 1 hour
    appOpen: 1000 * 60 * 60       // 1 hour
};

/**
 * Utility to manage AdMob Native Ads (Interstitial and App Open)
 */
export const AdMobManager = {
    /**
     * Initializes AdMob and preloads ads
     */
    init: async () => {
        if (!Capacitor.isNativePlatform()) return;

        try {
            await AdMob.initialize();
            await AdMobManager.prepareAll();
        } catch {
            console.error('AdMob Init Error');
        }
    },

    prepareAll: async () => {
        await Promise.all([
            AdMobManager.prepareInterstitial(),
            AdMobManager.prepareAppOpen()
        ]);
    },

    /**
     * Checks if enough time has passed since the last ad show
     */
    canShow: (type) => {
        const lastShow = localStorage.getItem(`admob_last_show_${type}`);
        if (!lastShow) return true;

        const now = Date.now();
        const diff = now - parseInt(lastShow);
        return diff > LIMITS[type];
    },

    /**
     * Records the time an ad was shown
     */
    recordShow: (type) => {
        localStorage.setItem(`admob_last_show_${type}`, Date.now().toString());
    },

    /**
     * Preloads an interstitial ad
     */
    prepareInterstitial: async () => {
        if (!Capacitor.isNativePlatform()) return;
        try {
            await AdMob.prepareInterstitial({
                adId: INTERSTITIAL_ID,
                isTesting: false
            });
        } catch {
            console.error('AdMob Interstitial Prepare Error');
        }
    },

    /**
     * Shows the preloaded interstitial ad
     */
    showInterstitial: async () => {
        if (!Capacitor.isNativePlatform()) return;
        if (!AdMobManager.canShow('interstitial')) return;

        try {
            await AdMob.showInterstitial();
            AdMobManager.recordShow('interstitial');
            AdMobManager.prepareInterstitial();
        } catch {
            console.error('AdMob Interstitial Show Error');
            AdMobManager.prepareInterstitial();
        }
    },

    /**
     * Preloads an App Open ad
     */
    prepareAppOpen: async () => {
        if (!Capacitor.isNativePlatform()) return;
        try {
            await AdMob.prepareAppOpenAd({
                adId: APP_OPEN_ID,
                isTesting: false
            });
        } catch {
            console.error('AdMob App Open Prepare Error');
        }
    },

    /**
     * Shows the preloaded App Open ad
     */
    showAppOpen: async () => {
        if (!Capacitor.isNativePlatform()) return;
        if (!AdMobManager.canShow('appOpen')) return;

        try {
            await AdMob.showAppOpenAd();
            AdMobManager.recordShow('appOpen');
            AdMobManager.prepareAppOpen();
        } catch {
            console.error('AdMob App Open Show Error');
            AdMobManager.prepareAppOpen();
        }
    }
};
